import React, { FC, memo, useState } from 'react';

import { Button, Result } from 'antd';
import { useTranslation } from 'react-i18next';

import ResultWithIcon from '@/components/Common/ResultWithIcon';
import LanguageProvider from '@/components/Common/LanguageProvider';

import ReportProblemModal from './ReportProblemModal';
import SecuringClockIcon from './SecuringClockIcon';
import { ErrorDiagnostics } from './reportText';
import { useFilePreparationCountdown } from './useFilePreparationCountdown';

import styles from './LoadErrorReportPage.module.scss';

const NAME_SPACE = 'general.loadErrorReport';

export type LoadErrorVariant = 'generic' | '408';

export interface LoadErrorReportPageProps {
    diagnostics: ErrorDiagnostics;
    /** Comma-joined helpdesk recipient(s) for the mailto "to" field. */
    helpdeskRecipients: string;
    /** User-facing error variant; defaults to generic load failure. */
    variant?: LoadErrorVariant;
    /** Required for variant 408 — anchors the preparation countdown in a cookie. */
    fileId?: string;
    /** Periodic silent retry while the 408 screen is shown; surfaces success or a non-408 failure. */
    onPreparationRetry?: () => void;
    /** When true, omits the full-viewport wrapper (for embedded surfaces like the recipient carousel). */
    compact?: boolean;
    /** Override the generic variant's title (e.g. "Unable to load link"); defaults to the file message. */
    genericTitle?: React.ReactNode;
}

/**
 * File load error screen. Generic failures show a primary "Report a problem" CTA;
 * specific codes (e.g. 408 file being secured) show a tailored message with an
 * optional countdown and report as a secondary action.
 */
const LoadErrorReportPage: FC<LoadErrorReportPageProps> = ({
    diagnostics,
    helpdeskRecipients,
    variant = 'generic',
    fileId,
    onPreparationRetry,
    compact = false,
    genericTitle,
}) => {
    const { t, i18n } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const { formatted: countdownFormatted, expired } = useFilePreparationCountdown(
        variant === '408' ? fileId : undefined,
        onPreparationRetry ?? ((): void => {}),
    );

    const reportProblemControl = (
        <Button
            type="link"
            className={styles.reportLink}
            onClick={() => setIsModalOpen(true)}
        >
            {t(`${NAME_SPACE}.reportProblemSecondary`)}
        </Button>
    );

    const modal = (
        <ReportProblemModal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            diagnostics={diagnostics}
            helpdeskRecipients={helpdeskRecipients}
        />
    );

    const renderContent = (): JSX.Element => {
        if (variant === '408' && !expired) {
            return (
                <Result
                    icon={<SecuringClockIcon />}
                    title={t(`${NAME_SPACE}.errors.408.title`)}
                    subTitle={(
                        <div className={styles.preparedSubTitle}>
                            <span className={styles.preparedText}>{t(`${NAME_SPACE}.errors.408.subtitle`)}</span>
                            <span className={styles.countdown}>
                                {t(`${NAME_SPACE}.errors.408.countdown`, { time: countdownFormatted })}
                            </span>
                        </div>
                    )}
                    extra={reportProblemControl}
                    className={styles.result}
                />
            );
        }

        return (
            <ResultWithIcon
                status="error"
                iconSize="large"
                title={genericTitle ?? t('general.specterxCommon.couldNotLoadFileTryAgain')}
                subTitle={t(`${NAME_SPACE}.subtitle`)}
                extra={(
                    <Button
                        type="primary"
                        onClick={() => setIsModalOpen(true)}
                    >
                        {t(`${NAME_SPACE}.reportProblem`)}
                    </Button>
                )}
                className={styles.result}
            />
        );
    };

    return (
        <LanguageProvider>
            <div
                className={compact ? styles.compactContainer : styles.container}
                dir={i18n.dir()}
            >
                {renderContent()}
                {modal}
            </div>
        </LanguageProvider>
    );
};

export default memo(LoadErrorReportPage);
